import { create } from 'zustand';
import { useItineraryStore } from './itinerary-store';
import { TOUR_STEPS, type TourCommand } from '../components/tour/tour-steps';

/**
 * Run-state store for the new-trip spotlight walkthrough — see ADR-0091.
 *
 * Deliberately a standalone, **ephemeral** Zustand store (no persist
 * middleware), NOT a slice of `itinerary-store`. The durable state lives
 * on the itinerary store's view-slice:
 *
 *  - `tutorialDisabled` (synced) — the "never show again" pref.
 *  - `tutorialPendingTripId` (local-persisted) — the trip whose tour is
 *    still pending. A manual create arms it; finishing / skipping /
 *    disabling clears it. Persisting it there means a force-quit mid-tour
 *    re-shows the tour on the next open of that trip, while keeping the
 *    transient run state (which step is showing) out of storage.
 *
 * The store also owns a one-way **command channel** (`pendingCommand`)
 * the overlay uses to switch the trip screen's tab without lifting that
 * state into a store. The trip screen subscribes and maps it to its own
 * `setActiveTab`. The tour never opens menus/panels (those fight the
 * overlay's portal z-order).
 */

export interface TourState {
  /** True while the tour overlay is visible. */
  isRunning: boolean;
  /** Index into `TOUR_STEPS` of the step currently showing. */
  stepIndex: number;
  /** Id of the trip the tour is running against (the overlay only
   *  renders on that trip's focused screen). */
  runTripId: string | null;
  /** Latest command for the trip screen to apply (switch tab). New
   *  object identity per step so the consumer effect re-fires. */
  pendingCommand: TourCommand | null;

  /** Begin the tour for `tripId` unconditionally (used by replay). */
  start: (tripId: string) => void;
  /** Advance; past the last step this completes the tour. */
  next: () => void;
  /** Step back; no-op at the first step. */
  back: () => void;
  /** Finish normally — clears the pending marker, stamps `tutorialSeenAt`. */
  complete: () => void;
  /** Abandon the tour. Clears the pending marker so it won't re-show for
   *  this trip, but a future new trip re-arms it (every-new-trip intent). */
  skip: () => void;
  /** Toggle the durable "don't show again" pref and end the run. */
  setDisabledForever: (disabled: boolean) => void;
  /** Force-start (the /profile "Replay" entry). Caller navigates to
   *  `tripId` first. */
  replay: (tripId: string) => void;
  /** Auto-start gate: starts only when the tour isn't durably disabled
   *  and this trip is the one armed by a manual create. */
  maybeAutoStart: (tripId: string) => void;
  /** Reset ephemeral run state. Called on logout and by tests. */
  clearTourForLogout: () => void;
}

/** Build the screen-command for a given step: which tab to show. The
 *  tour only switches tabs — it never opens menus/panels (those fight
 *  the overlay's portal z-order; see tour-steps module header). */
function commandForStep(index: number): TourCommand {
  const step = TOUR_STEPS[index];
  if (!step) return {};
  return { tab: step.tab };
}

export const useTourStore = create<TourState>()((set, get) => ({
  isRunning: false,
  stepIndex: 0,
  runTripId: null,
  pendingCommand: null,

  start: (tripId) =>
    set({
      isRunning: true,
      stepIndex: 0,
      runTripId: tripId,
      pendingCommand: commandForStep(0),
    }),

  next: () => {
    const { stepIndex } = get();
    if (stepIndex + 1 >= TOUR_STEPS.length) {
      get().complete();
      return;
    }
    const nextIndex = stepIndex + 1;
    set({ stepIndex: nextIndex, pendingCommand: commandForStep(nextIndex) });
  },

  back: () => {
    const { stepIndex } = get();
    if (stepIndex === 0) return;
    const prevIndex = stepIndex - 1;
    set({ stepIndex: prevIndex, pendingCommand: commandForStep(prevIndex) });
  },

  complete: () => {
    set({ isRunning: false, runTripId: null, pendingCommand: null });
    const itinerary = useItineraryStore.getState();
    itinerary.setTutorialPending(null);
    // Stamp last-seen (synced) so a future version-bumped re-tour can
    // tell who already finished. markTutorialSeen marks prefs dirty.
    itinerary.markTutorialSeen();
  },

  skip: () => {
    set({ isRunning: false, runTripId: null, pendingCommand: null });
    // Clear the pending marker so this trip won't re-show, but leave
    // `tutorialDisabled` alone — a future new trip still re-arms the tour.
    useItineraryStore.getState().setTutorialPending(null);
  },

  setDisabledForever: (disabled) => {
    const itinerary = useItineraryStore.getState();
    itinerary.setTutorialDisabled(disabled);
    if (disabled) {
      set({ isRunning: false, runTripId: null, pendingCommand: null });
      itinerary.setTutorialPending(null);
      itinerary.markTutorialSeen();
    }
  },

  replay: (tripId) => {
    get().start(tripId);
  },

  maybeAutoStart: (tripId) => {
    const itinerary = useItineraryStore.getState();
    if (itinerary.tutorialDisabled) return;
    if (get().isRunning) return;
    // Only the trip armed by a manual create auto-starts. The marker is
    // persisted, so a force-quit mid-tour re-arms this path on reopen.
    if (itinerary.tutorialPendingTripId !== tripId) return;
    get().start(tripId);
  },

  clearTourForLogout: () =>
    set({
      isRunning: false,
      stepIndex: 0,
      runTripId: null,
      pendingCommand: null,
    }),
}));

/** Test helper — reset in-memory state to defaults. */
export function _resetTourStore(): void {
  useTourStore.setState({
    isRunning: false,
    stepIndex: 0,
    runTripId: null,
    pendingCommand: null,
  });
}
